import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  ArrowUpDown,
  Filter,
  Columns,
  Check,
  X,
  Star,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useUserList } from '../hooks/useUsers';
import { UserListQueryParams, UserListItem } from '../types/user';
import {
  cn,
  formatRelativeTime,
  formatDateTime,
  formatNumber,
  formatPhoneNumber,
} from '../utils/helpers';
import { Avatar, Badge } from '../components/ui';

// ============================================================================
// Column Definitions
// ============================================================================

interface ColumnDef {
  key: keyof UserListItem;
  header: string;
  defaultVisible: boolean;
  sticky?: boolean;
  minWidth: number;
  render: (user: UserListItem) => React.ReactNode;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'name',
    header: 'User',
    defaultVisible: true,
    sticky: true,
    minWidth: 220,
    render: (u) => (
      <div className="flex items-center gap-2.5">
        <Avatar
          src={u.profileImage ?? undefined}
          name={u.name ?? undefined}
          size="sm"
        />
        <span className="font-medium text-foreground truncate">
          {u.name || 'Unnamed'}
        </span>
      </div>
    ),
  },
  {
    key: 'email',
    header: 'Email',
    defaultVisible: true,
    minWidth: 220,
    render: (u) => (
      <span className="text-muted-foreground truncate">
        {u.email || '--'}
      </span>
    ),
  },
  {
    key: 'phone',
    header: 'Phone',
    defaultVisible: false,
    minWidth: 160,
    render: (u) => (
      <span className="text-muted-foreground">
        {formatPhoneNumber(u.phone ?? undefined)}
      </span>
    ),
  },
  {
    key: 'isActive',
    header: 'Active',
    defaultVisible: true,
    minWidth: 80,
    render: (u) => (
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            u.isActive ? 'bg-emerald-400' : 'bg-red-400'
          )}
        />
        <span className="text-xs text-muted-foreground">
          {u.isActive ? 'Yes' : 'No'}
        </span>
      </span>
    ),
  },
  {
    key: 'isVerified',
    header: 'Verified',
    defaultVisible: true,
    minWidth: 90,
    render: (u) =>
      u.isVerified ? (
        <Check className="h-4 w-4 text-emerald-400" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40" />
      ),
  },
  {
    key: 'isProvider',
    header: 'Provider',
    defaultVisible: true,
    minWidth: 90,
    render: (u) =>
      u.isProvider ? (
        <Badge variant="default" size="sm">
          Provider
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground/40">--</span>
      ),
  },
  {
    key: 'rating',
    header: 'Rating',
    defaultVisible: true,
    minWidth: 90,
    render: (u) =>
      u.rating != null ? (
        <span className="flex items-center gap-1">
          <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
          <span className="text-foreground">{u.rating.toFixed(1)}</span>
        </span>
      ) : (
        <span className="text-muted-foreground/40">--</span>
      ),
  },
  {
    key: 'totalReviews',
    header: 'Reviews',
    defaultVisible: false,
    minWidth: 90,
    render: (u) => (
      <span className="text-muted-foreground">{formatNumber(u.totalReviews)}</span>
    ),
  },
  {
    key: 'followerCount',
    header: 'Followers',
    defaultVisible: false,
    minWidth: 100,
    render: (u) => (
      <span className="text-muted-foreground">{formatNumber(u.followerCount)}</span>
    ),
  },
  {
    key: 'connectionCount',
    header: 'Connections',
    defaultVisible: false,
    minWidth: 110,
    render: (u) => (
      <span className="text-muted-foreground">
        {formatNumber(u.connectionCount)}
      </span>
    ),
  },
  {
    key: 'chargesEnabled',
    header: 'Charges',
    defaultVisible: false,
    minWidth: 90,
    render: (u) =>
      u.chargesEnabled ? (
        <Check className="h-4 w-4 text-emerald-400" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40" />
      ),
  },
  {
    key: 'payoutsEnabled',
    header: 'Payouts',
    defaultVisible: false,
    minWidth: 90,
    render: (u) =>
      u.payoutsEnabled ? (
        <Check className="h-4 w-4 text-emerald-400" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40" />
      ),
  },
  {
    key: 'lastSeenAt',
    header: 'Last Seen',
    defaultVisible: true,
    minWidth: 120,
    render: (u) => (
      <span className="text-muted-foreground">
        {u.lastSeenAt ? formatRelativeTime(u.lastSeenAt) : '--'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Joined',
    defaultVisible: true,
    minWidth: 160,
    render: (u) => (
      <span className="text-muted-foreground">{formatDateTime(u.createdAt)}</span>
    ),
  },
];

const DEFAULT_VISIBLE = new Set(
  COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
);

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;

const SORT_OPTIONS: { label: string; sortBy: UserListQueryParams['sortBy']; sortOrder: UserListQueryParams['sortOrder'] }[] = [
  { label: 'Newest first', sortBy: 'createdAt', sortOrder: 'desc' },
  { label: 'Oldest first', sortBy: 'createdAt', sortOrder: 'asc' },
  { label: 'Recently active', sortBy: 'lastSeenAt', sortOrder: 'desc' },
  { label: 'Name A-Z', sortBy: 'name', sortOrder: 'asc' },
  { label: 'Name Z-A', sortBy: 'name', sortOrder: 'desc' },
  { label: 'Highest rated', sortBy: 'rating', sortOrder: 'desc' },
  { label: 'Lowest rated', sortBy: 'rating', sortOrder: 'asc' },
];

const FILTER_OPTIONS: { key: string; label: string; options: { label: string; value: string }[] }[] = [
  {
    key: 'isActive',
    label: 'Active',
    options: [
      { label: 'All', value: '' },
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ],
  },
  {
    key: 'isVerified',
    label: 'Verified',
    options: [
      { label: 'All', value: '' },
      { label: 'Verified', value: 'true' },
      { label: 'Unverified', value: 'false' },
    ],
  },
  {
    key: 'isProvider',
    label: 'Provider',
    options: [
      { label: 'All', value: '' },
      { label: 'Providers', value: 'true' },
      { label: 'Non-providers', value: 'false' },
    ],
  },
];

// ============================================================================
// Dropdown Hook (click-outside dismiss)
// ============================================================================

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return { open, setOpen, ref };
}

// ============================================================================
// UserExplorer Component
// ============================================================================

const UserExplorer: React.FC = () => {
  const navigate = useNavigate();

  // --- State ---
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortIdx, setSortIdx] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({
    isActive: '',
    isVerified: '',
    isProvider: '',
  });
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(DEFAULT_VISIBLE)
  );

  // Debounce search
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 300);
  }, []);

  // Build query params
  const sort = SORT_OPTIONS[sortIdx];
  const queryParams = useMemo<UserListQueryParams>(() => {
    const params: UserListQueryParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.isActive) params.isActive = filters.isActive;
    if (filters.isVerified) params.isVerified = filters.isVerified;
    if (filters.isProvider) params.isProvider = filters.isProvider;
    return params;
  }, [page, sort, debouncedSearch, filters]);

  const { items, total, loading, totalPages, refetch } = useUserList(queryParams);

  // Visible columns (always include sticky 'name')
  const activeCols = useMemo(
    () => COLUMNS.filter((c) => c.sticky || visibleCols.has(c.key)),
    [visibleCols]
  );

  // Dropdowns
  const colDropdown = useDropdown();
  const sortDropdown = useDropdown();

  // Column toggle helpers
  const toggleCol = (key: string) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showAllCols = () =>
    setVisibleCols(new Set(COLUMNS.map((c) => c.key)));

  const resetCols = () => setVisibleCols(new Set(DEFAULT_VISIBLE));

  const visibleCount = activeCols.length;

  // Pagination helpers
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const startRow = (page - 1) * PAGE_SIZE + 1;
  const endRow = Math.min(page * PAGE_SIZE, total);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-4 pb-3 space-y-3">
        {/* Back + Title */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/users')}
            className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-foreground">User Explorer</h1>
            {!loading && (
              <Badge variant="muted" size="sm">
                {formatNumber(total)}
              </Badge>
            )}
          </div>
          <button
            onClick={() => refetch()}
            className="ml-auto p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full h-8 pl-8 pr-3 text-sm rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {search && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Filter dropdowns */}
          {FILTER_OPTIONS.map((filter) => (
            <select
              key={filter.key}
              value={filters[filter.key] || ''}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, [filter.key]: e.target.value }));
                setPage(1);
              }}
              className="h-8 px-2.5 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
            >
              {filter.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === '' ? filter.label : opt.label}
                </option>
              ))}
            </select>
          ))}

          {/* Sort dropdown */}
          <div className="relative" ref={sortDropdown.ref}>
            <button
              onClick={() => sortDropdown.setOpen(!sortDropdown.open)}
              className="flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-lg border border-border bg-card text-foreground hover:bg-muted/50 transition-colors"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{sort.label}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
            {sortDropdown.open && (
              <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-lg border border-border bg-card shadow-lg py-1">
                {SORT_OPTIONS.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSortIdx(idx);
                      setPage(1);
                      sortDropdown.setOpen(false);
                    }}
                    className={cn(
                      'w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors',
                      idx === sortIdx
                        ? 'text-primary font-medium'
                        : 'text-foreground'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Column toggle dropdown */}
          <div className="relative" ref={colDropdown.ref}>
            <button
              onClick={() => colDropdown.setOpen(!colDropdown.open)}
              className="flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-lg border border-border bg-card text-foreground hover:bg-muted/50 transition-colors"
            >
              <Columns className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Columns</span>
              <Badge variant="muted" size="sm" className="ml-0.5 !px-1.5 !py-0">
                {visibleCount}
              </Badge>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
            {colDropdown.open && (
              <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-lg border border-border bg-card shadow-lg py-1">
                {/* Actions */}
                <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border">
                  <button
                    onClick={showAllCols}
                    className="text-[11px] text-primary hover:underline"
                  >
                    Show All
                  </button>
                  <span className="text-muted-foreground/40">|</span>
                  <button
                    onClick={resetCols}
                    className="text-[11px] text-primary hover:underline"
                  >
                    Reset
                  </button>
                </div>
                {COLUMNS.filter((c) => !c.sticky).map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visibleCols.has(col.key)}
                      onChange={() => toggleCol(col.key)}
                      className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
                    />
                    {col.header}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 min-h-0 overflow-auto px-4 sm:px-6">
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {/* Head */}
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {activeCols.map((col) => (
                    <th
                      key={col.key}
                      className={cn(
                        'text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground whitespace-nowrap',
                        col.sticky &&
                          'sticky left-0 z-10 bg-muted/30'
                      )}
                      style={{ minWidth: col.minWidth }}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {loading && items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeCols.length}
                      className="text-center py-20"
                    >
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                      <p className="text-xs text-muted-foreground mt-2">
                        Loading users...
                      </p>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeCols.length}
                      className="text-center py-20 text-muted-foreground text-sm"
                    >
                      No users found
                    </td>
                  </tr>
                ) : (
                  items.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => navigate(`/users/${user.id}`)}
                      className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors"
                    >
                      {activeCols.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            'px-3 py-2.5 whitespace-nowrap',
                            col.sticky &&
                              'sticky left-0 z-10 bg-card group-hover:bg-muted/20'
                          )}
                          style={{ minWidth: col.minWidth }}
                        >
                          {col.render(user)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 flex items-center justify-between border-t border-border">
          <span className="text-xs text-muted-foreground">
            {loading ? (
              'Loading...'
            ) : (
              <>
                {startRow}–{endRow} of {formatNumber(total)}
              </>
            )}
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-lg border border-border hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-muted-foreground px-2">
              {page} / {totalPages}
            </span>
            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-lg border border-border hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserExplorer;
